import { useTranslation, t } from '../../../i18n';
import React, { useEffect, useState } from 'react';
import {
    Button, Card, Checkbox, Form, InputNumber, Modal, Popconfirm, Select, Space, Tag, Tooltip, Typography, theme,
} from 'antd';
import { DeleteOutlined, ExpandOutlined, PlusOutlined, DisconnectOutlined } from '@ant-design/icons';
import Money from '../../Money';
import CompactTable from './CompactTable';
import { ok, runAction, errMsg } from './utils';

const { Text } = Typography;

const ioLimitText = (d) => {
    const parts = [];
    if (Number(d.total_iops_sec)) parts.push(`${d.total_iops_sec} IOPS`);
    if (Number(d.read_bytes_sec)) parts.push(t('Read {{value}} MB/s', { value: Math.round(d.read_bytes_sec / 1024 / 1024) }));
    if (Number(d.write_bytes_sec)) parts.push(t('Write {{value}} MB/s', { value: Math.round(d.write_bytes_sec / 1024 / 1024) }));
    return parts.length ? parts.join(' · ') : t('Unlimited');
};

const DiskTab = ({ id, vm, api, adapter, isMobile, disabled, reload, onInvoice }) => {
    const { t } = useTranslation();
    const { token } = theme.useToken();
    const isAdmin = adapter.isAdmin;
    const disks = vm.disks || [];

    const [addOpen, setAddOpen] = useState(false);
    const [resizeDisk, setResizeDisk] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [quote, setQuote] = useState(null); // {enabled, amount, currency, days, price_per_gb_month}
    const [quoteLoading, setQuoteLoading] = useState(false);
    const [addForm] = Form.useForm();
    const [resizeForm] = Form.useForm();
    const [isos, setIsos] = useState({ items: [], mounted: null, loaded: false, error: null });
    const [isoId, setIsoId] = useState();
    const sizeGb = Form.useWatch('size', addForm);

    const fetchQuote = async (size) => {
        setQuoteLoading(true);
        try {
            const res = await adapter.quoteDisk(id, size);
            setQuote(ok(res) ? res.data : { enabled: false });
        } catch (e) {
            setQuote({ enabled: false });
        } finally {
            setQuoteLoading(false);
        }
    };

    // Probe whether self-service purchase is open (user only).
    useEffect(() => {
        if (!isAdmin) fetchQuote(10);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, isAdmin]);

    // Re-quote when the size changes inside the purchase modal.
    useEffect(() => {
        if (isAdmin || !addOpen || !sizeGb) return undefined;
        const t = setTimeout(() => fetchQuote(sizeGb), 400);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sizeGb, addOpen, isAdmin]);

    const purchaseEnabled = isAdmin || quote?.enabled === true;

    const submitAdd = async (values) => {
        setSubmitting(true);
        try {
            if (isAdmin) {
                const res = await runAction(() => api.addDisk(id, { size: values.size, init: values.init ? 'yes' : 'no' }), t('Disk added'), t('Failed to add disk'));
                if (res) { setAddOpen(false); reload(); }
                return;
            }
            const res = await runAction(() => adapter.buyDisk(id, values.size), t('Order created'), t('Purchase failed'));
            if (res) {
                setAddOpen(false);
                if (res.data?.paid) reload();
                else if (res.data?.invoice_id) onInvoice(res.data.invoice_id);
            }
        } finally {
            setSubmitting(false);
        }
    };

    const submitResize = async (values) => {
        const d = resizeDisk;
        if (!d) return;
        setSubmitting(true);
        const res = await runAction(() => api.updateDisk(id, {
            disk_id: d.id,
            size: values.size,
            io: d.io || 'native',
            driver: d.driver || 'virtio',
            cache: d.cache || 'none',
            total_iops_sec: d.total_iops_sec || 0,
            read_bytes_sec: d.read_bytes_sec ? Math.round(d.read_bytes_sec / 1000 / 1000) : 0,
            write_bytes_sec: d.write_bytes_sec ? Math.round(d.write_bytes_sec / 1000 / 1000) : 0,
            read_iops_sec: d.read_iops_sec || 0,
            write_iops_sec: d.write_iops_sec || 0,
        }), t('Capacity expansion submitted. Takes effect after restart.'), t('Failed to expand capacity'));
        setSubmitting(false);
        if (res) { setResizeDisk(null); reload(); }
    };

    const deleteDisk = async (diskId) => {
        const res = await runAction(() => api.deleteDisk(id, diskId), t('Disk deleted'), t('Failed to delete disk'));
        if (res) reload();
    };

    const loadIsos = async () => {
        const res = await adapter.getIsos(id);
        if (ok(res)) setIsos({ items: res.data?.items || [], mounted: res.data?.mounted || null, loaded: true, error: null });
        else setIsos({ items: [], mounted: null, loaded: true, error: errMsg(res, t('Failed to load')) });
    };

    useEffect(() => {
        loadIsos();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const mountIso = async () => {
        if (!isoId) return;
        setSubmitting(true);
        const res = await runAction(() => adapter.mountIso(id, isoId), t('ISO mount request submitted'), t('Failed to mount ISO'));
        setSubmitting(false);
        if (res) { setIsoId(undefined); loadIsos(); }
    };

    const ejectIso = async () => {
        setSubmitting(true);
        const res = await runAction(() => adapter.ejectIso(id), t('Eject submitted'), t('Eject failed'));
        setSubmitting(false);
        if (res) loadIsos();
    };

    const columns = [
        {
            title: t('Type'), key: 'type', render: (_, d) => (
                <Tag color={d.type === 'system' ? 'blue' : 'green'}>{d.type === 'system' ? t('System disk') : t('Data disk')}</Tag>
            ),
        },
        { title: t('Device'), key: 'dev', render: (_, d) => <Text style={{ fontFamily: token.fontFamilyCode }}>{d.dev || '-'}</Text> },
        { title: t('Capacity'), key: 'size', render: (_, d) => `${d.size} GB` },
        { title: t('Driver / cache'), key: 'driver', render: (_, d) => `${d.driver || 'virtio'} / ${d.cache || 'none'}` },
        { title: t('IO limit'), key: 'io', render: (_, d) => ioLimitText(d) },
        ...(isAdmin ? [{
            title: t('Storage pool'), key: 'pool', render: (_, d) => (d.storage_pool
                ? <Tooltip title={d.storage_pool.path}>{d.storage_pool.name}</Tooltip>
                : '-'),
        }] : []),
        {
            title: '', key: 'actions', align: 'right', render: (_, d) => (d.type === 'system' ? null : (
                <Space size={4}>
                    {isAdmin && (
                        <Button
                            size="small"
                            type="text"
                            icon={<ExpandOutlined />}
                            disabled={disabled}
                            onClick={() => { setResizeDisk(d); resizeForm.setFieldsValue({ size: d.size }); }}
                        >
                            {t('Expand capacity')}
                        </Button>
                    )}
                    <Popconfirm title={t('Delete this disk? Data cannot be recovered.')} okButtonProps={{ danger: true }} onConfirm={() => deleteDisk(d.id)} disabled={disabled}>
                        <Button size="small" type="text" danger icon={<DeleteOutlined />} disabled={disabled}>{t('Delete')}</Button>
                    </Popconfirm>
                </Space>
            )),
        },
    ];

    const addButton = (
        <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            disabled={disabled || !purchaseEnabled}
            loading={!isAdmin && quoteLoading && !quote}
            onClick={() => { addForm.setFieldsValue({ size: 10, init: false }); setAddOpen(true); }}
        >
            {t('Add data disk')}
        </Button>
    );

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Card
                size="small"
                title={t('Disk')}
                extra={!purchaseEnabled ? <Tooltip title={t('Self-service purchasing is not yet available')}><span>{addButton}</span></Tooltip> : addButton}
            >
                <CompactTable isMobile={isMobile} columns={columns} dataSource={disks} emptyText={t('No disks yet')} />
            </Card>

            <Card size="small" title="ISO">
                {isos.mounted ? (
                    <Space wrap>
                        <Tag color="blue" style={{ marginInlineEnd: 0 }}>{isos.mounted.name}</Tag>
                        <Button size="small" icon={<DisconnectOutlined />} loading={submitting} disabled={disabled} onClick={ejectIso}>{t('Eject')}</Button>
                    </Space>
                ) : (
                    <Space.Compact style={{ width: isMobile ? '100%' : 420, maxWidth: '100%' }}>
                        <Select
                            style={{ flex: 1, minWidth: 0 }}
                            showSearch
                            optionFilterProp="label"
                            value={isoId}
                            onChange={setIsoId}
                            loading={!isos.loaded}
                            placeholder={isos.error || (isos.loaded && !isos.items.length ? t('No ISOs available') : t('Select ISO'))}
                            disabled={disabled || !isos.items.length}
                            options={isos.items.map((i) => ({ value: i.id, label: i.name }))}
                        />
                        <Button type="primary" loading={submitting} disabled={disabled || !isoId} onClick={mountIso}>{t('Mount')}</Button>
                    </Space.Compact>
                )}
            </Card>

            <Modal
                title={t('Add data disk')}
                open={addOpen}
                onCancel={() => setAddOpen(false)}
                onOk={() => addForm.submit()}
                okText={isAdmin ? t('Add') : t('Buy')}
                confirmLoading={submitting}
                okButtonProps={{ disabled: !isAdmin && quote?.enabled === false }}
                destroyOnClose
            >
                <Form form={addForm} layout="vertical" onFinish={submitAdd}>
                    <Form.Item name="size" label={t('Capacity (GB)')} rules={[{ required: true, message: t('Please enter a capacity') }]}>
                        <InputNumber min={1} max={2000} style={{ width: '100%' }} />
                    </Form.Item>
                    {isAdmin ? (
                        <Form.Item name="init" valuePropName="checked">
                            <Checkbox>{t('Hot-attach now')}</Checkbox>
                        </Form.Item>
                    ) : (
                        <div style={{ minHeight: 22 }}>
                            {quote?.enabled ? (
                                <Space size={8} wrap>
                                    <Text type="secondary">{t('Cost')}</Text>
                                    <Text strong><Money amount={quote.amount} currency={quote.currency} /></Text>
                                    {quote.days ? <Text type="secondary">{t('({{days}} days)', { days: quote.days })}</Text> : null}
                                    {quote.price_per_gb_month ? (
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            <Money amount={quote.price_per_gb_month} currency={quote.currency} /> {t(' / GB·month')}
                                        </Text>
                                    ) : null}
                                </Space>
                            ) : quoteLoading ? <Text type="secondary">{t('Calculating…')}</Text> : <Text type="secondary">{t('Self-service purchasing is not yet available')}</Text>}
                        </div>
                    )}
                </Form>
            </Modal>

            <Modal
                title={t('Expand capacity {{value}}', { value: resizeDisk?.dev || '' })}
                open={!!resizeDisk}
                onCancel={() => setResizeDisk(null)}
                onOk={() => resizeForm.submit()}
                confirmLoading={submitting}
                destroyOnClose
            >
                <Form form={resizeForm} layout="vertical" onFinish={submitResize}>
                    <Form.Item
                        name="size"
                        label={t('New capacity (GB)')}
                        tooltip={t('Can only be expanded. Takes effect after a restart; you must extend the partition in the operating system.')}
                        rules={[{ required: true, message: t('Please enter a capacity') }]}
                    >
                        <InputNumber min={resizeDisk?.size || 1} max={2000} style={{ width: '100%' }} />
                    </Form.Item>
                </Form>
            </Modal>
        </Space>
    );
};

export default DiskTab;
